import { LISTING_STATE_DRAFT } from '../../util/types';
import { isBookingProcessAlias } from '../../transactions/transaction';
import { isFaceMergeRetiredListing } from '../../util/listingFaces';

/**
 * Wizard tabs reachable via `/l/edit/:tab` (booking edit flow).
 * Keep in sync with `TABS_BOOKING_EDIT` in EditListingWizard.js.
 *
 * Defined as string literals here (not imported from EditListingWizardTab) so
 * this module can load from the Redux duck without pulling the whole edit
 * wizard into the initial bundle — that circular import blanked the app.
 */
export const EDIT_LISTING_SHORTCUT_TABS = [
  'typeofspace',
  'location',
  'capacity',
  'details',
  'pricing',
  'availability',
  'photos',
];

export const isEditListingShortcutTab = tab => EDIT_LISTING_SHORTCUT_TABS.includes(tab);

/**
 * Whether a listing is a pool (piscina), including the legacy `category` key.
 */
export const isPoolListing = listing => {
  const publicData = listing?.attributes?.publicData || {};
  return publicData.searchListingType === 'pool' || publicData.category === 'pool';
};

/**
 * Bookable listings that expose edit tabs (not drafts, nor gardens retired by the
 * face-merge, which EditListingPage refuses).
 */
export const isEligibleForListingEdit = listing => {
  const state = listing?.attributes?.state;
  const processAlias = listing?.attributes?.publicData?.transactionProcessAlias;
  return (
    state !== LISTING_STATE_DRAFT &&
    isBookingProcessAlias(processAlias) &&
    !isFaceMergeRetiredListing(listing)
  );
};

/**
 * Pick the listing to open for editing.
 * Prefers a pool; otherwise the first eligible bookable non-draft listing.
 *
 * @param {Array} listings Own listings from the SDK
 * @returns {Object|null} Chosen listing, or null if none are eligible
 */
export const pickListingForEdit = listings => {
  if (!Array.isArray(listings) || listings.length === 0) {
    return null;
  }

  const eligible = listings.filter(isEligibleForListingEdit);
  if (eligible.length === 0) {
    return null;
  }

  const pool = eligible.find(isPoolListing);
  return pool || eligible[0];
};
